/* ──────────────────────────────────────────────────────────────────────────
 * Apollo Bearing Index — omnibox query understanding (IFX.parse)
 * assets/interchange-parse.js
 *
 * Client mirror of lib/query-parse.ts: decides, without a network call,
 * whether the typed text is a part number, dimensions, a machine, or a code
 * to decode, so the intent chips light up as you type and Enter routes to
 * the right mode. The server's /api/intent is the tie-breaker for anything
 * ambiguous. Kept in sync with lib/query-parse.vectors.json.
 *
 *   IFX.parse.parseQuery("SKF 6205 2RS")  → { intent:"pn", brandHint:"skf", pn:"6205 2RS", … }
 *   IFX.parse.parseQuery("25x52x15")      → { intent:"dims", dims:{bore:25, od:52, width:15, unit:"mm"} }
 * ───────────────────────────────────────────────────────────────────────── */
(function () {
  "use strict";
  var IFX = window.IFX;
  if (!IFX || IFX.parse) return;

  var MM_PER_INCH = 25.4;

  var BRAND_ALIASES = {
    skf: "skf", fag: "fag", schaeffler: "fag", ina: "ina", nsk: "nsk", ntn: "ntn", timken: "timken", fafnir: "fafnir",
    torrington: "torrington", koyo: "koyo", jtekt: "koyo", nachi: "nachi", rhp: "rhp", mrc: "mrc", iko: "iko",
    mcgill: "mcgill", rbc: "rbc", dodge: "dodge", sealmaster: "sealmaster", browning: "browning", "link-belt": "link_belt",
    linkbelt: "link_belt", "link belt": "link_belt", rexnord: "rexnord", snr: "snr", consolidated: "consolidated",
    steyr: "steyr", kaydon: "kaydon", rollway: "rollway", hyatt: "hyatt", ndh: "ndh", "new departure": "new_departure",
    "norma hoffman": "norma_hoffman", riv: "riv", bower: "bower", bca: "bca", federal: "federal", iptci: "iptci",
    asahi: "asahi", ami: "ami", fyh: "fyh", peer: "peer", nke: "nke", zkl: "zkl", cooper: "cooper", thk: "thk",
    barden: "barden", nice: "nice", nadella: "nadella", urb: "urb", zwz: "zwz", hrb: "hrb", lyc: "lyc", general: "general",
  };
  var BRAND_KEYS = Object.keys(BRAND_ALIASES).sort(function (a, b) { return b.length - a.length; });

  var EQUIPMENT_MAKERS = [
    "haas", "mazak", "dmg mori", "mori seiki", "okuma", "doosan", "hyundai wia", "hardinge", "makino", "kitamura", "brother",
    "fanuc", "kuka", "abb", "yaskawa", "universal robots", "citizen", "tornos", "star", "tsugami", "studer", "sodick", "agie",
    "charmilles", "mitsubishi", "trumpf", "amada", "bystronic", "engel", "arburg", "husky", "milacron", "krauss-maffei",
    "wittmann", "caterpillar", "komatsu", "john deere", "deere", "case ih", "new holland", "kubota", "claas", "massey ferguson",
    "volvo", "hitachi", "liebherr", "bobcat", "jlg", "genie", "hyster", "crown", "toyota", "baldor", "weg", "marathon",
    "leeson", "reliance", "siemens", "ge", "sew-eurodrive", "sew eurodrive", "nord", "falk", "dodge", "browning", "goulds",
    "flowserve", "grundfos", "sulzer", "ksb", "wilo", "ingersoll rand", "ingersoll-rand", "atlas copco", "kaeser", "quincy",
    "sullair", "gardner denver", "vestas", "nordex", "enercon", "trane", "carrier", "york", "greenheck", "twin city fan",
    "new york blower", "hobart", "ford", "chevrolet", "chevy", "gmc", "dodge ram", "ram", "jeep", "honda", "nissan", "subaru",
    "hyundai", "kia", "bmw", "mercedes", "audi", "volkswagen", "vw", "polaris", "can-am", "yamaha", "kawasaki", "harley",
    "harley-davidson", "suzuki",
  ];
  function reEscape(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); }
  var MAKER_RE = new RegExp("^(" + EQUIPMENT_MAKERS.slice().sort(function (a, b) { return b.length - a.length; }).map(reEscape).join("|") + ")\\b\\s*(.*)$", "i");

  var DECODE_PHRASE_RE = /^(?:what\s+(?:does|is|do)\s+|decode\s+|meaning\s+of\s+|explain\s+|define\s+)(.+?)(?:\s+mean|\s+means|\s+stand\s+for)?\??$/i;
  var SUFFIX_ONLY_RE = /^(2RS1?|2RSR|2RSH|RS1?|RSR|ZZ|2Z|Z|ZR|2ZR|DDU|DU|LLU|LU|LLB|VV|C[0-5]|CN|CM|P[0246]|ABEC-?[13579]|W33|K30|K|NR|N|TN9?|TVP2?|TVH|M1?A?|MB|MA|EC[PJ]?|E1A?|E|2NSE|NSE|2NKE|EAE4|EAKE4)$/i;
  var NUM = "(\\d+(?:[.,]\\d+)?(?:[\\s-]+\\d+\\/\\d+)?|\\d+\\/\\d+)";
  var DIMS_X_RE = new RegExp("^" + NUM + "\\s*(?:x|×|\\*|by)\\s*" + NUM + "(?:\\s*(?:x|×|\\*|by)\\s*" + NUM + ")?\\s*(mm|in|inch|inches|\")?$", "i");
  var DIMS_LABELED_RE = /\b(bore|id|inner|od|outer|outside|width|w|b)\s*[:=]?\s*(\d+(?:[.,]\d+)?(?:\s*\d+\/\d+)?|\d+\/\d+)\s*(mm|in|inch|inches|")?/gi;
  var INCH_BORE_RE = /^(\d+(?:[.,]\d+)?|\d+\s*-?\s*\d+\/\d+|\d+\/\d+)\s*(?:"|in|inch|inches)\s*(?:bore|id|shaft)?$/i;
  var PN_SHAPE_RE = /^[A-Z]{0,5}\d{2,7}(?:[A-Z0-9./\-\s]{0,24})$/i;
  var SCHAEFFLER_TOKEN_RE = /(?:^|[-.\s])(E1A?|XL|TVP2?|TVPB|TVH|M1A?|MB1|MA1|MPA1?|MPB|MPAX|H40|J20AA|J20B|J20C|C-2HRS|2HRS|HRS|2BRS|BRS|C-2Z|C-Z|C-2BRS|K-C3|K30|E-K)(?=[-.\s/]|$)/i;

  function parseFractionInches(input) {
    if (input == null) return null;
    var s = String(input).trim().replace(/["”]$/, "").replace(/\s*in\.?$/i, "").trim();
    if (!s) return null;
    if (/^-?\d*\.\d+$/.test(s) || /^-?\d+$/.test(s)) { var n = Number(s); return Number.isFinite(n) ? n : null; }
    var m = /^(\d+)\s*\/\s*(\d+)$/.exec(s);
    if (m) return Number(m[2]) === 0 ? null : Number(m[1]) / Number(m[2]);
    m = /^(\d+)[\s\-]+(\d+)\s*\/\s*(\d+)$/.exec(s);
    if (m) return Number(m[3]) === 0 ? null : Number(m[1]) + Number(m[2]) / Number(m[3]);
    return null;
  }
  function inchesToMm(inches) { return Math.round(inches * MM_PER_INCH * 1000) / 1000; }
  function num(s) {
    if (/\//.test(s)) { var v = parseFractionInches(s.replace(/\s+/g, "-").replace(/--/g, "-")); if (v != null) return v; }
    return Number(String(s).replace(",", "."));
  }

  function splitBrandPrefix(raw) {
    var t = String(raw || "").trim();
    var lower = t.toLowerCase();
    for (var i = 0; i < BRAND_KEYS.length; i++) {
      var k = BRAND_KEYS[i];
      if (lower.indexOf(k) === 0) {
        var rest = t.slice(k.length);
        if (rest === "" || /^[\s\-:]/.test(rest)) {
          var cleaned = rest.replace(/^[\s\-:]+/, "");
          if (cleaned) return { brand: BRAND_ALIASES[k], rest: cleaned };
        }
      }
    }
    return { brand: null, rest: t };
  }

  function pretokenize(raw) {
    var s = String(raw || "").replace(/[‐-―−]/g, "-");
    var alias = null;
    var m = /\(\s*-?([A-Z0-9][A-Z0-9\-\/.]*)\s*\)/i.exec(s);
    if (m) {
      alias = m[1];
      s = s.replace(m[0], "");
    }
    s = s.replace(/\s+-\s*/g, "-").replace(/-\s+/g, "-").replace(/\s{2,}/g, " ").trim();
    var hints = {};
    if (alias) {
      var baseMatch = /^([A-Z]{0,4}\d{2,7}(?:\/\d{1,4})?)/i.exec(s);
      hints.alias = baseMatch && /^-/.test(alias) === false && !/^\d/.test(alias) ? baseMatch[1] + "-" + alias.replace(/^-/, "") : alias.replace(/^-/, "");
      if (baseMatch && !/^[A-Z]{0,4}\d{2,7}/i.test(alias)) hints.alias = baseMatch[1] + "-" + alias.replace(/^-/, "");
    }
    return { cleaned: s, hints: hints };
  }

  function parseDimsText(text) {
    var t = text.trim();
    var m = DIMS_X_RE.exec(t);
    if (m) {
      var unit = m[4] && /in|"/.test(m[4]) ? "in" : "mm";
      var conv = function (v) { return v == null ? null : unit === "in" ? inchesToMm(num(v)) : num(v); };
      return { bore: conv(m[1]), od: conv(m[2]), width: conv(m[3]), unit: unit };
    }
    m = INCH_BORE_RE.exec(t);
    if (m) {
      var inches = parseFractionInches(m[1].replace(/\s+/g, "-").replace(/--/g, "-"));
      if (inches != null) return { bore: inchesToMm(inches), od: null, width: null, unit: "in" };
    }
    var labeled = { bore: null, od: null, width: null, unit: "mm" };
    var found = false;
    DIMS_LABELED_RE.lastIndex = 0;
    var lm;
    while ((lm = DIMS_LABELED_RE.exec(t))) {
      var label = lm[1].toLowerCase();
      var u = lm[3] && /in|"/.test(lm[3]) ? "in" : "mm";
      var value = u === "in" ? (function () { var i = parseFractionInches(lm[2]); return i == null ? null : inchesToMm(i); })() : num(lm[2]);
      if (value == null || !Number.isFinite(value)) continue;
      if (u === "in") labeled.unit = "in";
      if (label === "bore" || label === "id" || label === "inner") labeled.bore = value;
      else if (label === "od" || label === "outer" || label === "outside") labeled.od = value;
      else labeled.width = value;
      found = true;
    }
    return found ? labeled : null;
  }

  function parseQuery(raw) {
    var trimmed = String(raw == null ? "" : raw).trim();
    var empty = { raw: trimmed, intent: "unknown", confidence: 0, pn: null, brandHint: null, brandHintSource: null,
      aliases: [], dims: null, equipment: null, decode: null, alternatives: [] };
    if (!trimmed) return empty;

    var dm = DECODE_PHRASE_RE.exec(trimmed);
    if (dm) {
      var inner = dm[1].trim();
      var words = inner.split(/\s+/);
      var last = words[words.length - 1];
      var code = /^[A-Z0-9][A-Z0-9\-\/.]*$/i.test(last) ? last.toUpperCase() : null;
      return Object.assign({}, empty, { intent: "decode", confidence: 0.95, decode: { code: code, text: inner } });
    }
    if (SUFFIX_ONLY_RE.test(trimmed)) {
      return Object.assign({}, empty, { intent: "decode", confidence: 0.85, decode: { code: trimmed.toUpperCase(), text: trimmed }, alternatives: ["pn"] });
    }
    var dims = parseDimsText(trimmed);
    if (dims && (dims.bore != null || dims.od != null || dims.width != null)) {
      return Object.assign({}, empty, { intent: "dims", confidence: DIMS_X_RE.test(trimmed) ? 0.95 : 0.8, dims: dims });
    }
    var em = MAKER_RE.exec(trimmed);
    if (em && em[2].trim()) {
      var model = em[2].trim();
      var looksLikeBearing = PN_SHAPE_RE.test(model) && /^\d{4,5}/.test(model) && !/[A-Z]{2,}-?\d/i.test(model);
      if (!looksLikeBearing) {
        return Object.assign({}, empty, { intent: "equipment", confidence: 0.85, equipment: { maker: em[1], model: model, text: trimmed }, alternatives: ["pn"] });
      }
    }
    var split = splitBrandPrefix(trimmed);
    var pre = pretokenize(split.rest);
    var brandHint = split.brand;
    var brandHintSource = brandHint ? "typed" : null;
    if (!brandHint && SCHAEFFLER_TOKEN_RE.test(pre.cleaned)) { brandHint = "fag"; brandHintSource = "sniffed"; }
    var aliases = pre.hints.alias ? [pre.hints.alias] : [];
    if (!/\d/.test(trimmed)) {
      return Object.assign({}, empty, { intent: em ? "equipment" : "unknown", confidence: em ? 0.6 : 0.2,
        equipment: em ? { maker: em[1], model: null, text: trimmed } : null, pn: pre.cleaned, alternatives: ["pn"] });
    }
    return Object.assign({}, empty, { intent: "pn", confidence: /\d{2,}/.test(pre.cleaned) ? 0.9 : 0.6, pn: pre.cleaned,
      brandHint: brandHint, brandHintSource: brandHintSource, aliases: aliases, alternatives: em ? ["equipment"] : [] });
  }

  var parse = {
    parseQuery: parseQuery,
    splitBrandPrefix: splitBrandPrefix,
    parseFractionInches: parseFractionInches,
    inchesToMm: inchesToMm,
    mmToInches: function (mm) { return Math.round((mm / MM_PER_INCH) * 1000) / 1000; },
    parseDimension: function (input, unit) {
      if (input == null) return null;
      var s = String(input).trim();
      if (!s) return null;
      if (unit === "mm") { var n = Number(s.replace(/\s*mm\s*$/i, "")); return Number.isFinite(n) ? n : null; }
      var inches = parseFractionInches(s);
      return inches === null ? null : inchesToMm(inches);
    },
  };
  IFX.parse = parse;
  IFX.define("parse", parse);
})();
